import { ChangeDetectionStrategy, Component, inject, Signal } from '@angular/core';
import {
  BottomNavbarComponent,
  OrAvatarComponent,
  OrIconComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import {
  AppRoutes,
  CopyValueToClipboardDirective,
  LocalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  RedirectToUserProfileDirective,
  TeamsRoutes,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TeamCardComponent } from '../team-card/team-card.component';
import { map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { ManageTeamMemberComponent } from '../manage-team-member/manage-team-member.component';
import { OpenSingleChatCommandHandler } from '@messages';
import { ManageTeamComponent } from '../manage-team/manage-team.component';
import { GetTeamDetailsQueryHandler, GetTeamDetailsQueryResult } from '../../application/handlers';
import { ShareTeamModalComponent } from '../share-team-modal/share-team-modal.component';
import { TeamInviteData } from '../../application/models';
import { TeamMember } from '../../application/interfaces';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-team-details',
  templateUrl: './team-details.component.html',
  styleUrls: ['./team-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    BottomNavbarComponent,
    TeamCardComponent,
    OrAvatarComponent,
    CopyValueToClipboardDirective,
    RedirectToUserProfileDirective,
    PageLayoutComponent,
    OrIconComponent
  ],
  providers: [OpenSingleChatCommandHandler, GetTeamDetailsQueryHandler, LocalRefreshService]
})
export class TeamDetailsComponent {
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly getTeamDetailsQueryHandler: GetTeamDetailsQueryHandler = inject(GetTeamDetailsQueryHandler);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);
  private readonly router: Router = inject(Router);

  private readonly teamDetails$: Observable<GetTeamDetailsQueryResult> = this.localRefreshService.refresh$.pipe(
    switchMap(() => this.modalProvider.showLoading$()),
    switchMap(() => this.activatedRoute.params),
    switchMap((params: Params) => this.getTeamDetailsQueryHandler.teamDetails(params['id'])),
    tap(() => this.modalProvider.dismissLoading()),
    shareReplay(1)
  );

  readonly teamDetails: Signal<GetTeamDetailsQueryResult | undefined> = toSignal(this.teamDetails$);

  readonly userId: Signal<string | undefined> = toSignal(
    this.userDataProvider.userData$.pipe(map((userData: UserModel) => userData.userId))
  );

  manageTeam(): void {
    const team: GetTeamDetailsQueryResult | undefined = this.teamDetails();
    if (!team) return;

    this.modalProvider.showModal$({
      component: ManageTeamComponent,
      cssClass: 'modal-auto',
      initialBreakpoint: 1,
      breakpoints: [0, 1],
      componentProps: { team }
    });
  }

  sendMessage(userId: string, displayName?: string): void {
    this.openSingleChatCommandHandler.open(userId, displayName).subscribe();
  }

  managePlayer(member: TeamMember, teamId: string): void {
    this.modalProvider
      .showModal$({
        component: ManageTeamMemberComponent,
        cssClass: 'modal-auto',
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        componentProps: { member, teamId }
      })
      .subscribe(() => this.localRefreshService.emit());
  }

  addMember(): void {
    const team: GetTeamDetailsQueryResult | undefined = this.teamDetails();
    if (!team) return;

    this.router.navigateByUrl(`${AppRoutes.TEAMS}/${team.teamId}/${team.invitationToken}/${TeamsRoutes.INVITE}`);
  }

  inviteViaLink(): void {
    const team: GetTeamDetailsQueryResult | undefined = this.teamDetails();
    if (!team) return;

    const payload: TeamInviteData = {
      teamId: team.teamId,
      token: team.invitationToken
    };

    this.modalProvider.showModal$({
      component: ShareTeamModalComponent,
      componentProps: { teamData: payload },
      cssClass: 'modal-auto',
      initialBreakpoint: 1,
      breakpoints: [0, 1]
    });
  }
}
